/**
 * Dominant speaker detection using audio levels from getStats().
 *
 * Polls audio levels every 100ms, applies exponential moving average
 * (EMA) smoothing, and maintains a ranked list of active speakers.
 * A speaker must exceed a threshold for a hysteresis window before
 * promotion to avoid rapid switching.
 */

/** EMA smoothing factor -- higher values weight recent samples more */
const EMA_ALPHA = 0.7

/** Minimum audio level to consider as speech (0.0 to 1.0) */
const SPEECH_THRESHOLD = 0.01

/** Time in ms a speaker must exceed threshold before promotion */
const HYSTERESIS_MS = 1500

/** Polling interval in ms */
const POLL_INTERVAL_MS = 100

/** Default number of top speakers to track */
const DEFAULT_TOP_N = 3

/** Per-speaker tracking state */
interface SpeakerState {
  /** EMA-smoothed audio level */
  smoothedLevel: number
  /** Timestamp when speaker first exceeded threshold continuously */
  aboveThresholdSince: number | null
  /** Whether this speaker has been promoted (exceeded hysteresis) */
  promoted: boolean
}

/** Audio receiver registration for monitoring */
interface AudioSource {
  /** Peer ID that this audio source belongs to */
  peerId: string
  /** The RTCRtpReceiver to poll for audio levels */
  receiver: RTCRtpReceiver
}

/** Internal module state */
let pollIntervalId: ReturnType<typeof setInterval> | null = null
const speakerStates = new Map<string, SpeakerState>()
const audioSources: AudioSource[] = []
const changeListeners: Array<(speakers: string[]) => void> = []
let lastActiveSpeakers: string[] = []

/**
 * Register an audio receiver for speaker detection.
 * Call this when a remote audio track is received.
 */
export function addAudioSource(
  peerId: string,
  receiver: RTCRtpReceiver,
): void {
  // Avoid duplicates for the same peer
  removeAudioSource(peerId)
  audioSources.push({ peerId, receiver })

  if (!speakerStates.has(peerId)) {
    speakerStates.set(peerId, {
      smoothedLevel: 0,
      aboveThresholdSince: null,
      promoted: false,
    })
  }
}

/**
 * Remove an audio receiver when a peer disconnects.
 */
export function removeAudioSource(peerId: string): void {
  const idx = audioSources.findIndex((s) => s.peerId === peerId)
  if (idx !== -1) {
    audioSources.splice(idx, 1)
  }
  speakerStates.delete(peerId)
}

/**
 * Start the speaker detection polling loop.
 * Polls getSynchronizationSources() every 100ms on all registered audio receivers.
 */
export function startSpeakerDetection(): void {
  if (pollIntervalId !== null) return

  pollIntervalId = setInterval(() => {
    pollAudioLevels()
  }, POLL_INTERVAL_MS)
}

/**
 * Stop the speaker detection polling loop.
 */
export function stopSpeakerDetection(): void {
  if (pollIntervalId !== null) {
    clearInterval(pollIntervalId)
    pollIntervalId = null
  }
  speakerStates.clear()
  audioSources.length = 0
  lastActiveSpeakers = []
}

/**
 * Get the current active speakers, ranked by smoothed audio level.
 * @param count - Maximum number of speakers to return (default 3)
 * @returns Array of peerIds, highest energy first
 */
export function getActiveSpeakers(count: number = DEFAULT_TOP_N): string[] {
  const promoted: Array<{ peerId: string; level: number }> = []

  for (const [peerId, state] of speakerStates) {
    if (state.promoted && state.smoothedLevel >= SPEECH_THRESHOLD) {
      promoted.push({ peerId, level: state.smoothedLevel })
    }
  }

  // Sort by smoothed level descending
  promoted.sort((a, b) => b.level - a.level)

  return promoted.slice(0, count).map((s) => s.peerId)
}

/**
 * Register a callback for active speaker changes.
 * Fires when the set of promoted speakers or their ranking changes.
 * @returns Unsubscribe function
 */
export function onActiveSpeakerChange(
  cb: (speakers: string[]) => void,
): () => void {
  changeListeners.push(cb)
  return () => {
    const idx = changeListeners.indexOf(cb)
    if (idx !== -1) changeListeners.splice(idx, 1)
  }
}

/**
 * Internal: poll audio levels from all registered receivers
 * and update speaker states with EMA smoothing.
 */
function pollAudioLevels(): void {
  const now = Date.now()

  for (const source of audioSources) {
    const state = speakerStates.get(source.peerId)
    if (!state) continue

    // Get current audio level from getSynchronizationSources
    let audioLevel = 0
    try {
      const syncSources = source.receiver.getSynchronizationSources()
      if (syncSources.length > 0) {
        // audioLevel is 0.0 (silence) to 1.0 (max) per RFC 6464
        audioLevel = syncSources[0].audioLevel ?? 0
      }
    } catch {
      // Receiver may have been removed -- skip
      continue
    }

    // Apply EMA smoothing
    state.smoothedLevel =
      EMA_ALPHA * audioLevel + (1 - EMA_ALPHA) * state.smoothedLevel

    // Hysteresis logic for speaker promotion
    if (state.smoothedLevel >= SPEECH_THRESHOLD) {
      if (state.aboveThresholdSince === null) {
        state.aboveThresholdSince = now
      } else if (
        !state.promoted &&
        now - state.aboveThresholdSince >= HYSTERESIS_MS
      ) {
        state.promoted = true
      }
    } else {
      // Level dropped below threshold -- reset hysteresis
      state.aboveThresholdSince = null
      // Demote after sustained silence
      if (state.smoothedLevel < SPEECH_THRESHOLD * 0.5) {
        state.promoted = false
      }
    }
  }

  // Check if active speakers changed and notify listeners
  const currentSpeakers = getActiveSpeakers()
  if (!arraysEqual(currentSpeakers, lastActiveSpeakers)) {
    lastActiveSpeakers = currentSpeakers
    for (const listener of changeListeners) {
      listener(currentSpeakers)
    }
  }
}

/**
 * Compare two string arrays for equality (same elements in same order).
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}
